/**
 * The scorecard editor (PRD §5.2).
 *
 * Six criteria, 0–5 each, with the weighted total updating as scores change.
 * The live total is a preview computed locally — the server's value is
 * authoritative and replaces it on save.
 *
 * Radio groups rather than a select or a slider: six discrete values are
 * faster to hit than a dropdown, and a slider makes an exact integer hard to
 * choose. Each group is labelled with its criterion and weight, so the reader
 * knows what a 5 is worth before choosing one.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 6.6, PRD §5.2
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Scorecard } from '@nexus/contracts';

import { BandBadge } from '../../../components/domain/BandBadge';
import { ConflictDialog } from '../../../components/domain/ConflictDialog';
import { Button } from '../../../components/ui/Button';
import { Card, CardBody, CardHead } from '../../../components/ui/Card';
import { Input } from '../../../components/ui/Input';
import { Kicker } from '../../../components/ui/Kicker';
import { formatDate, formatNumber, formatPercent } from '../../../lib/format';
import { useConflict } from '../../../lib/use-conflict';
import { useSaveScorecard } from '../api/startups.queries';
import { liveTotal } from '../scorecard-total';

/** The 0–5 scale of PRD §5.2. */
const SCALE = [0, 1, 2, 3, 4, 5] as const;

export interface ScorecardEditorProps {
  startupId: string;
  scorecard: Scorecard;
  /** False for a viewer: the scores are shown but not editable. */
  canEdit: boolean;
  /** Refetches the scorecard, so a conflict can be resolved in place. */
  onReload: () => void;
}

export function ScorecardEditor({
  startupId,
  scorecard,
  canEdit,
  onReload,
}: ScorecardEditorProps): React.JSX.Element {
  const { t } = useTranslation();
  const saveScorecard = useSaveScorecard(startupId);
  const { conflict, reportIfConflict, clear: clearConflict } = useConflict();

  const [scores, setScores] = useState<Record<string, number | undefined>>(() =>
    Object.fromEntries(
      scorecard.scores.map((entry) => [entry.criterionKey, entry.score]),
    ),
  );
  const [notes, setNotes] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      scorecard.scores.map((entry) => [entry.criterionKey, entry.note ?? '']),
    ),
  );
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const live = useMemo(
    () => liveTotal(scorecard.criteria, scores),
    [scorecard.criteria, scores],
  );

  /* An empty criteria set means the admin configuration is malformed. The
     server refuses to score in that state, so the editor says so rather than
     rendering a form whose submission would be rejected. */
  if (scorecard.criteria.length === 0) {
    return (
      <p role="alert" className="text-sem-bad text-sm">
        {t('scorecard.unavailable')}
      </p>
    );
  }

  const handleSave = (): void => {
    setSaveError(null);
    setSaved(false);

    const payload = {
      /* A type predicate on the filter, so the narrowing survives into the
         map and no assertion is needed. */
      scores: Object.entries(scores)
        .filter((entry): entry is [string, number] => entry[1] !== undefined)
        .map(([criterionKey, score]) => {
          /* An absent key and an empty string both mean "no note written", so
             the note is omitted rather than sent as ''. */
          const note = notes[criterionKey] ?? '';

          return {
            criterionKey,
            score,
            ...(note === '' ? {} : { note }),
          };
        }),
      version: scorecard.version,
    };

    saveScorecard.mutate(payload, {
      onSuccess: () => {
        setSaved(true);
      },
      onError: (error) => {
        /* A conflict gets the dedicated dialog rather than an inline message:
           "try again" is wrong advice when someone else has already scored the
           startup, and the remedy is to reload and see their scores
           (PRD §6.2). */
        if (reportIfConflict(error)) return;

        setSaveError(t('scorecard.failed'));
      },
    });
  };

  return (
    <Card>
      <CardHead
        actions={
          canEdit ? (
            <Button onClick={handleSave} disabled={saveScorecard.isPending} size="sm">
              {saveScorecard.isPending ? t('scorecard.saving') : t('scorecard.save')}
            </Button>
          ) : undefined
        }
      >
        <Kicker as="h2">{t('startup.tabs.scorecard')}</Kicker>
      </CardHead>

      <CardBody className="gap-s4 flex flex-col">
        <p className="text-body text-[13px] font-light">{t('scorecard.intro')}</p>

        <ul className="gap-s4 flex flex-col">
          {scorecard.criteria.map((criterion) => (
            <li key={criterion.key}>
              {/* A fieldset, so the criterion name is announced with each radio
                rather than the reader hearing six bare numbers. */}
              <fieldset>
                <legend className="mb-1.5 flex w-full flex-wrap items-baseline gap-2">
                  <span className="text-navy-text font-display text-[13px] font-bold">
                    {criterion.label}
                  </span>
                  <span className="text-body text-xs font-light">
                    {t('scorecard.weight', {
                      percent: formatPercent(criterion.weight * 100),
                    })}
                  </span>

                  {/* The score as a bar as well as a number. `aria-hidden`: the
                    radio group beside it already announces the chosen value,
                    and a progressbar repeating it would double every
                    criterion in a screen reader's output. */}
                  <span
                    aria-hidden="true"
                    className="bg-gray-100 ml-auto h-1.5 w-[104px] flex-none overflow-hidden rounded-full"
                  >
                    <span
                      className="bg-blue block h-full rounded-full transition-[width]"
                      style={{
                        width: `${String(((scores[criterion.key] ?? 0) / 5) * 100)}%`,
                      }}
                    />
                  </span>
                </legend>

                <div className="flex flex-wrap gap-1">
                  {SCALE.map((value) => (
                    <label
                      key={value}
                      className={
                        scores[criterion.key] === value
                          ? 'bg-blue min-h-11 min-w-11 cursor-pointer rounded-md px-3 text-center text-sm leading-11 text-white'
                          : 'border-gray-200 text-navy-text min-h-11 min-w-11 cursor-pointer rounded-md border px-3 text-center text-sm leading-11'
                      }
                    >
                      <input
                        type="radio"
                        name={criterion.key}
                        value={value}
                        checked={scores[criterion.key] === value}
                        disabled={!canEdit}
                        onChange={() => {
                          setScores((current) => ({
                            ...current,
                            [criterion.key]: value,
                          }));
                          setSaved(false);
                        }}
                        /* Visually hidden rather than `display: none`: a hidden
                         input is unfocusable, which would take the whole group
                         out of the keyboard order.

                         No `aria-label`: one per radio made all six announce
                         identically, so the value could not be distinguished.
                         The wrapping label's text supplies the value, and the
                         fieldset's legend supplies the criterion. */
                        className="sr-only"
                      />
                      {formatNumber(value)}
                    </label>
                  ))}
                </div>

                <label className="mt-2 block">
                  <span className="text-gray-400 text-xs">{t('scorecard.note')}</span>
                  <Input
                    value={notes[criterion.key] ?? ''}
                    disabled={!canEdit}
                    placeholder={t('scorecard.notePlaceholder')}
                    onChange={(event) => {
                      setNotes((current) => ({
                        ...current,
                        [criterion.key]: event.target.value,
                      }));
                      setSaved(false);
                    }}
                  />
                </label>
              </fieldset>
            </li>
          ))}
        </ul>

        {/* The weighted total, at the prototype's 48px. The figure IS the
            output of the exercise, so it gets the display treatment and the
            green that §4.2 reserves for data values. */}
        <div className="border-gray-100 gap-s3 flex flex-wrap items-baseline border-t-2 pt-s3">
          {/* `aria-live` so the total is announced as scores change, not only
              seen — the figure is the point of the exercise. */}
          <b
            aria-live="polite"
            className="font-display text-green-dark text-5xl leading-none font-extrabold tracking-[-0.02em] tabular-nums"
          >
            {live.total === null
              ? t('scorecard.missing', { count: live.missingCount })
              : formatNumber(live.total, { maximumFractionDigits: 2 })}
          </b>
          <span className="text-body text-xs font-light">{t('scorecard.outOf')}</span>

          <span className="ml-auto flex items-center gap-2">
            <span className="text-body text-xs">{t('scorecard.band')}</span>
            <BandBadge band={live.band} />
          </span>
        </div>

        {scorecard.scoredAt === null ? null : (
          <p className="text-body text-xs font-light">
            {t('scorecard.scoredBy', { when: formatDate(scorecard.scoredAt) })}
          </p>
        )}

        {saveError === null ? null : (
          <p role="alert" className="text-sem-bad text-sm">
            {saveError}
          </p>
        )}

        {/* `role="status"` rather than `alert`: a success is worth announcing
            but must not interrupt. */}
        {saved ? (
          <p role="status" className="text-sem-ok text-sm">
            {t('scorecard.saved')}
          </p>
        ) : null}
      </CardBody>

      <ConflictDialog
        open={conflict !== null}
        submittedVersion={conflict?.submittedVersion}
        onDismiss={clearConflict}
        onReload={() => {
          clearConflict();
          onReload();
        }}
      />
    </Card>
  );
}
